import React, { useEffect, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import { fetchReportShelterIntakesExits } from "../../../services/httpClient";
import { Card, Row, Spinner } from "react-bootstrap";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ShelterOccupancyLineChartState {
  data?: any[];
}

export const ShelterOccupancyLineChart = ({ data }: ShelterOccupancyLineChartState) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [current, setCurrent] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (data) {
        const chartData = getOccupancyPerDay(data);
        setCurrent(chartData);
        setIsLoading(false);
      } else {
        await getOccupancyData();
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getLast30Days = () => {
    const dates = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      dates.push(d.toISOString().slice(0, 10)); // 'YYYY-MM-DD'
    }
    return dates;
  };

  const getOccupancyData = async () => {
    await fetchReportShelterIntakesExits()
      .then((res: any) => {
        const data = res[0].data;
        const chartData = getOccupancyPerDay(data);
        setCurrent(chartData);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getOccupancyPerDay = (users: any) => {
    const days = getLast30Days();

    return days.map((dateStr) => {
      const count = users.filter((user: any) => {
        if (!user.intake_at) return false; // Skip if no intake date
        const intake = new Date(user.intake_at);
        const exit = user.exit_at ? new Date(user.exit_at) : null;
        const day = new Date(dateStr);

        return intake <= day && (!exit || exit > day);
      }).length;

      return { date: dateStr, count };
    });
  };

  const maxScale = Math.max(...current.map((d) => d.count)) + 5 || 10; // Default to 10 if scale is not provided
  const minScale = Math.min(...current.map((d) => d.count)) - 5 || 0; // Default to 10 if scale is not provided

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "30 Day Occupancy",
      },
    },
    scales: {
      y: {
        min: minScale,
        max: maxScale,
      },
      x: {
        ticks: {
          autoSkip: false, // Prevent automatic skipping of labels
          maxRotation: 30, // Rotate labels at a 45-degree angle
          minRotation: 30, // Also set the minimum rotation angle
          callback: function (value: any, index: number, ticks: any) {
            return index % 5 === 0 ? chartData.labels[index] : index === 29 ? chartData.labels[index] : "";
          },
        },
      },
    },
  };

  const chartData = {
    labels: current?.map((item: any) => item?.date),
    datasets: [
      {
        label: "Population",
        data: current?.map((item: any) => item?.count),
        borderColor: "rgb(218 , 126 , 57)",
        backgroundColor: "rgba(218 , 126 , 57, 0.5)",
      },
    ],
  };

  if (isLoading) {
    return (
      <Card className="p-3">
        <Card.Body>
          <Row className="text-center">
            <text className="mt-2 mb-2">
              <Spinner animation="border" style={{ color: "#F6893D" }} />
            </text>
          </Row>
        </Card.Body>
      </Card>
    );
  } else {
    return (
      <Card className="p-3">
        <Line options={options} data={chartData} />
      </Card>
    );
  }
};
